import formatDate from '../../utils/format-date';
import timecodeSeconds from './timecode-seconds';
import explicitIcon from '../assets/icons/explicit.svg';

export function metadataTitle (metadata, locale) {
  if (!metadata) return '';

  if (metadata.contentType === 'stream') {
    return locale.lang('player.live_stream');
  }

  if (metadata.contentType === 'nowplaying') {
    if (metadata.creator && metadata.title) {
      return `${metadata.creator} - ${metadata.title}`;
    } else if (metadata.title) {
      return metadata.title;
    }
  }

  return metadata.title || '';
}

export function metadataSubTitle (metadata, locale) {
  if (!metadata) return '';

  let subtitle = metadata.grouping || '';

  if (metadata.contentType === 'stream') {
    subtitle = metadata.creator || metadata.title;
  } else if (['podcast'].indexOf(metadata.contentType) >= 0) {
    if (metadata.publishDate) {
      subtitle = `${formatDate(metadata.publishDate, 'j M ga', locale)}${subtitle ? ` / ${subtitle}` : ''}`;
    }
  }

  if (metadata.explicit) {
    return `${subtitle} <span class="iono-metadata-explicit" title="${locale.lang('player.explicit')}">${explicitIcon}</span>`;
  }

  return subtitle;
}

const artworkSizes = ['small', 'medium', 'large'];
export function metadataArtwork (metadata, size = 'small') {
  if (!metadata) {
    return '';
  }

  const icons = (metadata.icons || []);
  const iconMap = {};
  for (const icon of icons) {
    if (icon.size === size) {
      return icon.url;
    }
    iconMap[icon.size] = icon.url;
  }

  for (const size of artworkSizes) {
    if (iconMap[size]) return iconMap[size];
  }

  return '';
}

export function metadataDescription (metadata) {
  if (!(metadata && metadata.description)) return '';

  return metadata.description
    // links
    .replace(/(www|http:|https:)+[^\s]+[\w\\/]/gm, (match) => {
      const url = match.substring(0, 4) !== 'http' ? `http://${match}` : match;
      return `<a href="${url}" class="iono-link" rel="nofollow" target="_blank">${match}</a>`;
    })
    // email
    .replace(/[\w]+@[\w]+(?:\.[\w]+)*/gm, (match) => {
      return `<a href="mailto:${match}" class="iono-link" rel="nofollow" target="_blank">${match}</a>`;
    })
    // timecode links
    .replace(/([0-9]+(:[0-5]?[0-9])(:[0-5]?[0-9](\.[0-9]+)?)?)/gm, (match) => {
      return `<a nohref class="iono-link" data-action="seek-seconds" data-action-args="${timecodeSeconds(match)}">${match}</a>`;
    });
}

/**
 * Get the playlist item website url from metadata
 *
 * @param  {Object} metadata
 * @return {String|null}
 */
export function metadataUrl (metadata) {
  if (!metadata.url) {
    return null;
  }

  return metadata.url;
}

export function metadataResolve (player, locale) {
  const playlistMetadata = player.getPlaylistMetadata();
  const playlistItemMetadata = player.getMetadata();

  let artwork, title, subtitle;

  if (player.isLive()) {
    const nowPlayingMetadata = player.getNowPlayingItem();

    artwork = metadataArtwork(nowPlayingMetadata, 'small') ||
      metadataArtwork(playlistItemMetadata, 'small') ||
      metadataArtwork(playlistMetadata, 'small');

    title = metadataTitle(nowPlayingMetadata, locale) ||
      metadataTitle(playlistItemMetadata, locale) ||
      metadataTitle(playlistMetadata, locale) ||
      locale.lang('player.live_stream');

    subtitle = metadataSubTitle(nowPlayingMetadata, locale) ||
      metadataSubTitle(playlistItemMetadata, locale) ||
      metadataSubTitle(playlistMetadata, locale);

    return { title, subtitle, artwork };
  }

  artwork = metadataArtwork(playlistItemMetadata, 'small') ||
    metadataArtwork(playlistMetadata, 'small');

  title = metadataTitle(playlistItemMetadata, locale) ||
    metadataTitle(playlistMetadata, locale);

  subtitle = metadataSubTitle(playlistItemMetadata, locale) ||
    metadataSubTitle(playlistMetadata, locale);

  return { title, subtitle, artwork };
}
